import { currentOrganization } from "./route-workspace";

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || "").replace(
  /\/$/,
  "",
);

export function apiUrl(path) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${normalizedPath}`;
}

let pendingRefresh = null;

export async function apiFetch(path, options) {
  // Old browser drafts predate opaque references. Do not resend their private
  // source fields; leave the draft intact and ask the user to reload its saved copy.
  if (
    typeof options?.body === "string" &&
    /^[[{]/.test(options.body.trim()) &&
    /^(?:\/)?api\/(?:route(?:s)?)(?:\/|$)/.test(path)
  ) {
    const payload = JSON.parse(options.body);
    const legacy = (value) =>
      value &&
      typeof value === "object" &&
      ((Object.hasOwn(value, "portalUrl") && value.portalUrl) ||
        (["TX", "KY", "IL", "MS", "IN", "GA", "CO", "OR", "VA", "RI"].includes(
          value.state,
        ) &&
          (value.permitId || value.url) &&
          !value.referenceId) ||
        Object.values(value).some(legacy));
    if (legacy(payload))
      throw new Error(
        "This older browser draft needs updated route references. Reopen the saved trip, or reupload its permits. Your draft has not been discarded.",
      );
  }
  const method = (options?.method || "GET").toUpperCase();
  const headers = new Headers(options?.headers);
  if (
    /^\/?api\/(?:routes?(?:\/|\?|$)|reroute(?:\?|$)|extract-pdf(?:\?|$)|shared-route\/import(?:\?|$))/.test(
      path,
    )
  ) {
    const organization = currentOrganization();
    if (organization) headers.set("X-Organization-Id", organization);
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method))
    headers.set("X-Requested-With", "PermitPilot");
  const request = {
    credentials: "include",
    ...options,
    ...(Array.from(headers.keys()).length ? { headers } : {}),
  };
  let res = await fetch(apiUrl(path), request);
  const authPath = path.replace(/^\//, "").startsWith("api/auth/");
  const canRefresh = !authPath || path.replace(/^\//, "") === "api/auth/me";
  if (res.status === 401 && canRefresh && options?.credentials !== "omit") {
    if (!pendingRefresh) {
      pendingRefresh = fetch(apiUrl("/api/auth/refresh"), {
        method: "POST",
        credentials: "include",
        headers: { "X-Requested-With": "PermitPilot" },
        signal: AbortSignal.timeout(15000),
      })
        .then((response) => response.ok)
        .catch(() => false)
        .finally(() => {
          pendingRefresh = null;
        });
    }
    const refreshed = await pendingRefresh;
    if (refreshed && !options?.signal?.aborted) {
      // A 401 rejects the action before authorization. Replay once, never loop.
      res = await fetch(apiUrl(path), request);
    }
  }

  if (res.status === 401 && !authPath) {
    window.dispatchEvent(new Event("pp-session-expired"));
  }

  if (res.status === 403 && headers.has("X-Organization-Id")) {
    const data = await res
      .clone()
      .json()
      .catch(() => null);
    if (data?.code === "ROUTE_OWNERSHIP")
      window.dispatchEvent(
        new CustomEvent("pp-workspace-access-denied", {
          detail: { organizationId: headers.get("X-Organization-Id") },
        }),
      );
  }

  return res;
}

/**
 * Safely parse a response as JSON, handling cases where the server
 * returns HTML error pages (e.g. CloudFront 504) instead of JSON.
 */
export async function safeJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    // CloudFront/ALB returned an HTML error page
    if (text.includes("504 Gateway Timeout") || text.includes("Gateway Time")) {
      throw new Error(
        "Request timed out. Your route is still processing — please wait and try again in a moment.",
      );
    }
    if (text.includes("502 Bad Gateway")) {
      throw new Error(
        "Server is temporarily unavailable. Please try again in a minute.",
      );
    }
    if (text.includes("503")) {
      throw new Error(
        "Service is temporarily busy. Please wait a moment and try again.",
      );
    }
    throw new Error(
      "Server returned an unexpected response. Please try again.",
    );
  }
}
